/**
 * The reports surface (P7-T2).
 *
 * The compact overview, the report scopes with their charts, the category breakdown and the
 * selected-date forecast — the reporting half of P7, kept in its own component so the ledger's
 * category and entry surfaces (P7-T1's half) can change without either one rewriting the other.
 *
 * What it shows, and where each figure comes from:
 *
 *   - **one block per native currency**, with income, expenses, the net between them and the
 *     recorded/pending split of each. There is no control and no element that adds two currencies
 *     together, because the payload has no field such a figure could come from (`RPT-05`, `FX-04`);
 *   - **two charts per currency** — the period's income/expense/net, and the categories by their own
 *     sizes — drawn as plain SVG rectangles in the page's own units, with the values in the labels
 *     as well, so the chart is a second reading of a number and never the only one (`UQ-07`);
 *   - **a category table** including the uncategorized group and archived categories marked as such,
 *     each with its recorded and pending halves (`CAT-02`, `CAT-01`, `RPT-03`);
 *   - **the account directory with one expandable forecast each**, run to the selected date, showing
 *     the recorded balance, pending outgoing, expected incoming and the explicit planned transfers,
 *     plus the overdue subset drawn once, and the pool's own statement of what it cannot know yet
 *     (`REC-05`, `REC-06`, `RPT-04`).
 *
 * Every figure is rendered exactly as the API computed it: the surface formats and lays out, it
 * never re-derives. That is what makes "the totals on screen equal the same figure computed from
 * persisted rows" a property of the code rather than a hope, and the acceptance suite asserts it
 * against a live database.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { AccountBody, LedgerForecastEnvelope, LedgerReportsEnvelope } from '@saldo/contracts';

import { describeLedgerFailure, listAccounts, type LedgerFailureKind } from './client';
import { fetchForecast, fetchReports } from './reportsClient';
import {
  barGeometry,
  categoryBars,
  categoryLabel,
  coverageText,
  forecastBreakdown,
  hasOverdue,
  initialScope,
  overdueRows,
  reportsPath,
  scopeDescription,
  summaryBars,
  type ChartBar,
  type ReportScopeState,
} from './reportsView';
import { formatBalance, formatMoneyMagnitude } from './money';
import { useAuthenticatedSession } from '../session/SessionBoundary';

/** The failure kind of anything the queries can throw; anything else is the server being away. */
function failureKind(error: unknown): LedgerFailureKind {
  const kind = (error as { kind?: LedgerFailureKind }).kind;
  return kind ?? 'unavailable';
}

export function ReportsWorkspace() {
  const session = useAuthenticatedSession();
  const [scope, setScope] = useState<ReportScopeState | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // The same read the ledger surface makes — one owner-scoped list, shared cache, and the business
  // day as the API stated it rather than as this browser's clock guesses it.
  const accountsQuery = useQuery({
    queryKey: ['ledger', 'accounts'],
    queryFn: ({ signal }) => listAccounts(session.csrfToken, signal),
  });
  const accounts: readonly AccountBody[] = accountsQuery.data?.accounts ?? [];
  const businessToday = accountsQuery.data?.meta.businessToday ?? null;

  const effective = useMemo(
    () => scope ?? initialScope(businessToday ?? new Date().toISOString().slice(0, 10)),
    [scope, businessToday],
  );

  const reportsQuery = useQuery<LedgerReportsEnvelope>({
    queryKey: ['reports', reportsPath(effective)],
    queryFn: ({ signal }) => fetchReports(reportsPath(effective), signal),
    enabled: businessToday !== null,
  });

  const forecastQuery = useQuery<LedgerForecastEnvelope>({
    queryKey: ['forecast', expanded, effective.date],
    queryFn: ({ signal }) => fetchForecast(`/api/v1/forecast?accountId=${expanded}&through=${effective.date}`, signal),
    enabled: expanded !== null,
  });

  const body = reportsQuery.data?.data;
  const update = (patch: Partial<ReportScopeState>): void => {
    setScope({ ...effective, ...patch });
  };

  return (
    <section className="panel" aria-labelledby="reports-heading" data-testid="reports">
      <h2 id="reports-heading">Reports</h2>

      <div className="toolbar" data-testid="report-controls">
        <label className="field">
          <span>Scope</span>
          <select
            data-testid="report-scope"
            value={effective.kind}
            onChange={(event) => update({ kind: event.target.value as ReportScopeState['kind'] })}
          >
            <option value="month">Month</option>
            <option value="range">Range</option>
            <option value="all">All periods</option>
          </select>
        </label>
        {effective.kind === 'month' ? (
          <label className="field">
            <span>Month</span>
            <input
              type="month"
              data-testid="report-month"
              value={effective.month}
              onChange={(event) => update({ month: event.target.value })}
            />
          </label>
        ) : null}
        {effective.kind === 'range' ? (
          <>
            <label className="field">
              <span>From</span>
              <input
                type="date"
                data-testid="report-from"
                value={effective.from}
                onChange={(event) => update({ from: event.target.value })}
              />
            </label>
            <label className="field">
              <span>To</span>
              <input
                type="date"
                data-testid="report-to"
                value={effective.to}
                onChange={(event) => update({ to: event.target.value })}
              />
            </label>
          </>
        ) : null}
        <label className="field">
          <span>Accounts</span>
          <select
            data-testid="report-role"
            value={effective.role}
            onChange={(event) => update({ role: event.target.value as ReportScopeState['role'] })}
          >
            <option value="all">Cash and cards</option>
            <option value="cash">Cash only</option>
            <option value="card">Cards only</option>
          </select>
        </label>
        <label className="field">
          <span>Project to</span>
          <input
            type="date"
            data-testid="forecast-date"
            value={effective.date}
            onChange={(event) => update({ date: event.target.value })}
          />
        </label>
        <button type="button" data-testid="report-reset" onClick={() => setScope(null)}>
          Reset
        </button>
      </div>

      <p className="meta" data-testid="report-scope-label">
        Showing {scopeDescription(effective)}
        {body === undefined ? '' : ` · ${body.period.label}`}
        {body === undefined ? '' : ` · recorded at ${body.cutoff}`}
      </p>

      {reportsQuery.isError ? (
        <p className="notice bad" data-testid="reports-error">
          {describeLedgerFailure(failureKind(reportsQuery.error))}
        </p>
      ) : null}

      {body !== undefined && body.blocks.length === 0 ? (
        <p data-testid="reports-empty">Nothing to report in this scope yet.</p>
      ) : null}

      {body?.blocks.map((block) => (
        <article className="report-block" key={block.currency} data-testid={`report-block-${block.currency}`}>
          <h3>{block.currency}</h3>
          <dl className="summary-grid">
            <div>
              <dt>Income</dt>
              <dd className="money" data-testid={`report-income-${block.currency}`}>
                {formatMoneyMagnitude(BigInt(block.totals.incomeMinor), block.currency)}
              </dd>
            </div>
            <div>
              <dt>Expenses</dt>
              <dd className="money" data-testid={`report-expense-${block.currency}`}>
                {formatMoneyMagnitude(BigInt(block.totals.expenseMinor), block.currency)}
              </dd>
            </div>
            <div>
              <dt>Net</dt>
              <dd className="money" data-testid={`report-net-${block.currency}`}>
                {formatBalance(BigInt(block.totals.netMinor), block.currency)}
              </dd>
            </div>
            <div>
              <dt>Recorded</dt>
              <dd className="money" data-testid={`report-recorded-${block.currency}`}>
                {formatBalance(BigInt(block.totals.recorded.netMinor), block.currency)}
              </dd>
            </div>
            <div>
              <dt>Pending</dt>
              <dd className="money" data-testid={`report-pending-${block.currency}`}>
                {formatBalance(BigInt(block.totals.pending.netMinor), block.currency)}
              </dd>
            </div>
          </dl>

          <Chart
            testId={`report-chart-${block.currency}`}
            label={`${block.currency} income, expenses and net for ${body.period.label}`}
            bars={summaryBars(block.totals)}
            currency={block.currency}
          />

          {block.categories.length > 0 ? (
            <Chart
              testId={`report-category-chart-${block.currency}`}
              label={`${block.currency} categories by size for ${body.period.label}`}
              bars={categoryBars(block)}
              currency={block.currency}
            />
          ) : null}

          <table className="report-categories" data-testid={`report-categories-${block.currency}`}>
            <caption>{`${block.currency} by category, ${body.period.label}`}</caption>
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">Entries</th>
                <th scope="col">Income</th>
                <th scope="col">Expenses</th>
                <th scope="col">Net</th>
                <th scope="col">Pending</th>
              </tr>
            </thead>
            <tbody>
              {block.categories.map((row) => (
                <tr key={row.categoryId ?? 'uncategorized'} data-testid="report-category-row">
                  <th scope="row" data-testid="report-category-label">
                    {categoryLabel(row)}
                  </th>
                  <td data-testid="report-category-entries">{row.entryCount}</td>
                  <td className="money" data-testid="report-category-income">
                    {formatMoneyMagnitude(BigInt(row.totals.incomeMinor), block.currency)}
                  </td>
                  <td className="money" data-testid="report-category-expense">
                    {formatMoneyMagnitude(BigInt(row.totals.expenseMinor), block.currency)}
                  </td>
                  <td className="money" data-testid="report-category-net">
                    {formatBalance(BigInt(row.totals.netMinor), block.currency)}
                  </td>
                  <td className="money" data-testid="report-category-pending">
                    {formatBalance(BigInt(row.totals.pending.netMinor), block.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      ))}

      <section aria-labelledby="forecast-heading" data-testid="forecast">
        <h3 id="forecast-heading">Forecast by account</h3>
        <p className="meta" data-testid="forecast-horizon">
          Projecting to {effective.date}
        </p>
        {accounts.length === 0 ? (
          <p data-testid="forecast-empty">No accounts yet.</p>
        ) : (
          <ul className="directory-list" data-testid="forecast-accounts">
            {accounts.map((account) => (
              <li key={account.id} data-testid="forecast-account">
                <div>
                  <span data-testid="forecast-account-name">{account.name}</span>{' '}
                  <span className="account-state" data-testid="forecast-account-state">
                    {account.kind === 'card' ? 'card' : 'cash'}
                  </span>
                </div>
                <span className="money" data-testid="forecast-account-balance">
                  {formatBalance(BigInt(account.balance.minorUnits), account.currency)}
                </span>
                <button
                  type="button"
                  data-testid="forecast-toggle"
                  aria-expanded={expanded === account.id}
                  onClick={() => setExpanded(expanded === account.id ? null : account.id)}
                >
                  {expanded === account.id ? 'Hide forecast' : 'Forecast'}
                </button>

                {expanded === account.id ? (
                  <div className="forecast-panel" data-testid="forecast-panel">
                    {forecastQuery.isError ? (
                      <p className="notice bad" data-testid="forecast-error">
                        {forecastQuery.error instanceof Error
                          ? forecastQuery.error.message
                          : 'The forecast could not be read.'}
                      </p>
                    ) : forecastQuery.data === undefined ? (
                      <p data-testid="forecast-loading">Reading the forecast…</p>
                    ) : (
                      <>
                        <p data-testid="forecast-headline">
                          <strong className="money" data-testid="forecast-projected">
                            {formatBalance(
                              BigInt(forecastQuery.data.data.projectedMinor),
                              forecastQuery.data.data.currency,
                            )}
                          </strong>{' '}
                          projected through {forecastQuery.data.data.through}
                        </p>
                        <dl className="breakdown" data-testid="forecast-breakdown">
                          {forecastBreakdown(forecastQuery.data.data).map((row) => (
                            <div key={row.testId}>
                              <dt>{row.label}</dt>
                              <dd className="money" data-testid={row.testId}>
                                {formatBalance(BigInt(row.minor), forecastQuery.data!.data.currency)}
                              </dd>
                              <dd className="note">{row.note}</dd>
                            </div>
                          ))}
                        </dl>
                        {hasOverdue(forecastQuery.data.data) ? (
                          <dl className="breakdown" data-testid="forecast-overdue">
                            {overdueRows(forecastQuery.data.data).map((row) => (
                              <div key={row.testId}>
                                <dt>{row.label}</dt>
                                <dd className="money" data-testid={row.testId}>
                                  {formatBalance(BigInt(row.minor), forecastQuery.data!.data.currency)}
                                </dd>
                                <dd className="note">{row.note}</dd>
                              </div>
                            ))}
                          </dl>
                        ) : null}
                        <p className="unknown" data-testid="forecast-coverage">
                          {coverageText(forecastQuery.data.data)}
                        </p>
                      </>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

/**
 * A chart of bars, drawn as SVG in the page's own coordinates.
 *
 * The value is printed beside every bar and each bar carries its amount in `aria-label`, so the
 * chart adds a reading rather than replacing one, and a reviewer (or a test) can assert the figure
 * without reading pixels.
 */
function Chart({
  testId,
  label,
  bars,
  currency,
}: {
  readonly testId: string;
  readonly label: string;
  readonly bars: readonly ChartBar[];
  readonly currency: string;
}) {
  const geometry = barGeometry(bars);
  return (
    <figure className="chart" data-testid={testId}>
      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        preserveAspectRatio="none"
        width="100%"
        height={geometry.height}
      >
        {geometry.bars.map((bar) => (
          <rect
            key={`${bar.testId}-${bar.label}`}
            data-testid={`${testId}-bar`}
            data-kind={bar.kind}
            data-minor={bar.magnitudeMinor.toString()}
            aria-label={`${bar.label} ${formatMoneyMagnitude(bar.magnitudeMinor, currency)}`}
            className={`bar ${bar.kind}`}
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
          />
        ))}
      </svg>
      <figcaption>
        <ul className="chart-legend">
          {geometry.bars.map((bar) => (
            <li key={`${bar.testId}-legend-${bar.label}`} data-testid="chart-legend-row">
              <span>{bar.label}</span>{' '}
              <span className="money" data-testid={`chart-value-${bar.kind}`}>
                {formatMoneyMagnitude(bar.magnitudeMinor, currency)}
              </span>
            </li>
          ))}
        </ul>
      </figcaption>
    </figure>
  );
}
